import { FunctionComponent } from 'react';
import styled, { css } from 'styled-components';

import { useTranslation } from 'next-i18next';
import Theme from '../../styles/Theme';
import { DirectionProfiles } from '../../models/MapBox';
import { convertMinuteToHour } from '../../utils/time';

import { IconOnBikeSVG } from './svg/IconOnBikeSVG';
import { IconOnFootSVG } from './svg/IconOnFootSVG';
import { IconOnVehiculeSVG } from './svg/IconOnVehiculeSVG';

interface Props {
	directionProfile: DirectionProfiles;
	value: number;
	selected: boolean;
	onClick: (directionProfile: DirectionProfiles) => void;
}

const StyledDistance = styled.div<{selected: boolean}>`
	display: flex;
	align-items: center;
	justify-content: center;
	height: ${(props) => 32 / props.theme.fontSizeBase}rem;
	gap: ${(props) => 4 / props.theme.fontSizeBase}rem;

	border-radius: ${(props) => 48 / props.theme.fontSizeBase}rem;
	padding: ${(props) => 2 / props.theme.fontSizeBase}rem ${(props) => 10 / props.theme.fontSizeBase}rem ${(props) => 2 / props.theme.fontSizeBase}rem ${(props) => 8 / props.theme.fontSizeBase}rem;

	color: ${(props) => props.theme.black};

	${(props) => props.selected && css`
		color: ${props.theme.white};
		background-color: ${props.theme.distanceSelected};
	`};
`;

const StyledNumber = styled.span`
	font-family: 'Plus Jakarta Sans', sans serif;
	font-style: normal;
	font-weight: 600;
	font-size: ${(props) => 15 / props.theme.fontSizeBase}rem;
	line-height: ${(props) => 20 / props.theme.fontSizeBase}rem;
	margin-right: ${(props) => 2 / props.theme.fontSizeBase}rem;
`;

const StyledUnit = styled.span`
	font-family: 'Plus Jakarta Sans', sans serif;
	font-style: normal;
	font-weight: 500;
	font-size: ${(props) => 11 / props.theme.fontSizeBase}rem;
	line-height: ${(props) => 19 / props.theme.fontSizeBase}rem;
	padding-top: ${(props) => 2 / props.theme.fontSizeBase}rem;
`;

export const DistanceIconValue: FunctionComponent<Props> = ({
	directionProfile, value, selected = false, onClick,
}: Props) => {
	const { t } = useTranslation(['common']);

	return (
		<StyledDistance selected={selected} onClick={() => onClick(directionProfile)}>
			{ directionProfile === 'walking' && <IconOnFootSVG width={20} height={20} color={selected ? Theme.white : Theme.black}/> }
			{ directionProfile === 'cycling' && <IconOnBikeSVG width={20} height={20} color={selected ? Theme.white : Theme.black}/> }
			{ directionProfile === 'driving' && <IconOnVehiculeSVG width={20} height={20} color={selected ? Theme.white : Theme.black}/> }

			{ value < 60 && <>
				<StyledNumber>{ Math.round(value) }</StyledNumber>
				<StyledUnit>{ t('common:min') }</StyledUnit>
			</> }
			{ value >= 60 && <>
				<StyledNumber>{ convertMinuteToHour(value) }</StyledNumber>
				<StyledUnit>
					{ convertMinuteToHour(value) === 1 ? t('common:hour') : t('common:hours')}
				</StyledUnit>
			</> }
		</StyledDistance>
	);
};
